import React, { useEffect, useState, useCallback } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { ujmsPretrialDashboard, ujmsLogCompliance, ujmsPretrialReferrals, ujmsCompletePretrialReferral } from "../../services/ujmsApi";
import { Panel, Stat, Badge, Seg, thCls, tdCls, inputCls, Field, btnPrimary, btnGhost, btnDanger, fmtDT, fmtD, EmptyState, can } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ClipboardCheck, AlertTriangle, ShieldCheck } from "lucide-react";

export default function PretrialWorkspace() {
  const ctx = useOutletContext();
  const access = ctx?.access;
  const canAssess = access ? can(access, "pretrial.assess") : true;
  const [d, setD] = useState(null);
  const [refs, setRefs] = useState([]);
  const [refFilter, setRefFilter] = useState("PENDING");
  const [logging, setLogging] = useState(null); // {condition, status}
  const loadRefs = useCallback(() => { ujmsPretrialReferrals().then((r) => setRefs(r.data)).catch(() => setRefs([])); }, []);
  const load = useCallback(() => { ujmsPretrialDashboard().then((r) => setD(r.data)).catch(() => {}); loadRefs(); }, [loadRefs]);
  useEffect(() => { load(); }, [load]);
  const completeRef = async (r) => {
    try { await ujmsCompletePretrialReferral(r.id); toast.success(`${r.defendant_name} — SAFE-T referral marked assessed`); loadRefs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not complete referral"); }
  };

  return (
    <div className="space-y-5" data-testid="pretrial-workspace">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Pretrial Services</h1>
        <p className="text-sm text-slate-500">Risk assessments, release-condition supervision and compliance reporting.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="SAFE-T Referrals Due" value={refs.filter((r) => r.status === "PENDING").length} testId="pt-stat-referrals" tone={refs.some((r) => r.overdue) ? "text-red-700" : refs.some((r) => r.status === "PENDING") ? "text-amber-700" : undefined} />
        <Stat label="Awaiting Assessment" value={d?.awaiting_assessment_count} testId="pt-stat-awaiting" tone={d?.awaiting_assessment_count ? "warn" : undefined} />
        <Stat label="Conditions Supervised" value={d?.active_conditions_count} testId="pt-stat-conditions" />
        <Stat label="Violations (30 days)" value={d?.violations_30d_count} testId="pt-stat-violations" />
        <Stat label="Open Tasks" value={d?.open_tasks_count} testId="pt-stat-tasks" />
      </div>

      <Panel title="SAFE-T Act pretrial referrals — arrest packets received from law enforcement (PSA due before the next court morning)" testId="pt-referrals"
        action={<><Seg options={[["PENDING", "Pending"], ["ASSESSED", "Assessed"], ["", "All"]]} value={refFilter} onChange={setRefFilter} testId="pt-ref-filter" /><button className={btnGhost} onClick={loadRefs} data-testid="pt-ref-refresh">Refresh</button></>}>
        {refs.filter((r) => !refFilter || r.status === refFilter).length === 0 ? (
          <EmptyState icon={ShieldCheck} title="No referrals" note="New arrest packets from the police API hub appear here automatically (725 ILCS 5/110)." testId="pt-referrals-empty" />
        ) : (
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Due by</th><th className={thCls}>Defendant</th><th className={thCls}>Booking</th><th className={thCls}>Agency</th><th className={thCls}>Charges</th><th className={thCls}>Custody</th><th className={thCls}>Known to court</th><th className={thCls}>Case</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
            <tbody>
              {refs.filter((r) => !refFilter || r.status === refFilter).map((r) => (
                <tr key={r.id} className={`hover:bg-slate-50 ${r.overdue ? "bg-red-50/50" : ""}`} data-testid={`pt-referral-${r.id}`}>
                  <td className={`${tdCls} font-mono text-xs`}>{fmtDT(r.due_by)}{r.overdue && <span className="ml-1 text-[9.5px] font-bold bg-red-600 text-white px-1 rounded-sm">OVERDUE</span>}</td>
                  <td className={`${tdCls} font-semibold`}>{r.defendant_name}<div className="text-[10px] text-slate-500 font-normal">DOB {r.dob || "—"}</div></td>
                  <td className={`${tdCls} font-mono text-xs`}>{r.booking_number}</td>
                  <td className={tdCls}>{r.agency}</td>
                  <td className={`${tdCls} text-xs`}>{(r.charges || []).join("; ")}{r.highest_class && <div className="text-[10px] text-slate-500">Highest: Class {r.highest_class}</div>}</td>
                  <td className={tdCls}>{r.in_custody ? <span className="text-[9.5px] font-bold bg-red-600 text-white px-1 rounded-sm">IN CUSTODY</span> : <span className="text-xs text-slate-500">released</span>}</td>
                  <td className={`${tdCls} text-xs`}>{r.identity_summary ? `${r.identity_summary.open_cases} open · ${r.identity_summary.active_warrants} warrant(s) · ${r.identity_summary.probation} probation` : "—"}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{r.case_id ? <Link className="text-blue-900 hover:underline" to={`/cases/${r.case_id}?tab=Pretrial`}>{r.case_number}</Link> : <span className="text-slate-400">awaiting Felony Review</span>}</td>
                  <td className={tdCls}><Badge value={r.status} />{r.assessed_by && <div className="text-[10px] text-slate-500">{r.assessed_by}</div>}</td>
                  <td className={tdCls}>{r.status === "PENDING" && canAssess && <button className={btnPrimary} onClick={() => completeRef(r)} data-testid={`pt-referral-complete-${r.id}`}>Mark assessed</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2 space-y-4">
          <Panel title="Cases awaiting risk assessment" testId="pt-awaiting">
            {(d?.awaiting_assessment || []).length === 0 ? (
              <EmptyState icon={ClipboardCheck} title="All caught up" note="Every open criminal case has a submitted assessment." testId="pt-awaiting-empty" />
            ) : (
              <table className="w-full border-collapse">
                <thead><tr><th className={thCls}>Case</th><th className={thCls}>Defendant</th><th className={thCls}>Filed</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
                <tbody>
                  {(d?.awaiting_assessment || []).map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50" data-testid={`pt-awaiting-${c.case_number}`}>
                      <td className={`${tdCls} font-mono text-xs`}>
                        <Link className="text-blue-900 hover:underline" to={`/ujms/cases/${c.id}?tab=Pretrial`}>{c.case_number}</Link>
                        <div className="text-slate-400 truncate max-w-[240px]">{c.caption}</div>
                      </td>
                      <td className={tdCls}>{c.primary_defendant || "—"}</td>
                      <td className={`${tdCls} font-mono text-xs`}>{c.filing_date}</td>
                      <td className={tdCls}><Badge value={c.status} /></td>
                      <td className={tdCls}>
                        <Link className={btnPrimary} to={`/ujms/cases/${c.id}?tab=Pretrial`} data-testid={`pt-assess-${c.case_number}`}>Assess</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
          <Panel title="Supervised release conditions" testId="pt-conditions">
            {(d?.active_conditions || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">No active conditions under supervision.</div>
            ) : (
              <table className="w-full border-collapse">
                <thead><tr><th className={thCls}>Case</th><th className={thCls}>Condition</th><th className={thCls}>Status</th><th className={thCls}>Last Check</th><th className={thCls}></th></tr></thead>
                <tbody>
                  {(d?.active_conditions || []).map((x) => (
                    <tr key={x.id} className="hover:bg-slate-50" data-testid={`pt-condition-${x.id}`}>
                      <td className={`${tdCls} font-mono text-xs`}>
                        <Link className="text-blue-900 hover:underline" to={`/ujms/cases/${x.case_id}?tab=Pretrial`}>{x.case_number}</Link>
                      </td>
                      <td className={tdCls}>{x.condition_label}{x.details && <div className="text-xs text-slate-400">{x.details}</div>}</td>
                      <td className={tdCls}><Badge value={x.status} /></td>
                      <td className={`${tdCls} text-xs`}>
                        {x.last_compliance ? <><Badge value={x.last_compliance.status} /> <span className="font-mono text-slate-400">{fmtD(x.last_compliance.created_at)}</span></> : "—"}
                      </td>
                      <td className={tdCls}>
                        <div className="flex gap-1.5">
                          <button className={btnGhost} onClick={() => setLogging({ condition: x, status: "COMPLIANT" })} data-testid={`pt-log-compliant-${x.id}`}>Compliant</button>
                          <button className={btnDanger} onClick={() => setLogging({ condition: x, status: "VIOLATION" })} data-testid={`pt-log-violation-${x.id}`}>Violation</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
        <div className="space-y-4">
          <Panel title="Recent violations (30 days)" testId="pt-violations">
            {(d?.recent_violations || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">No violations reported.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {(d?.recent_violations || []).map((v) => (
                  <div key={v.id} className="px-3 py-2 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-mono text-xs text-blue-900">{v.case_number}</div>
                      <div className="text-slate-700 text-xs">{v.note || "Violation reported"}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{fmtDT(v.created_at)} · {v.logged_by}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
          <Panel title="Pretrial tasks" testId="pt-tasks">
            {(d?.tasks || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">Queue is clear.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {(d?.tasks || []).map((t) => (
                  <div key={t.id} className="px-3 py-2 text-sm">
                    <div className="text-slate-800">{t.title}</div>
                    <div className="text-xs text-slate-400">Due {t.due_date} · <Badge value={t.priority} /></div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>

      <ComplianceDialog x={logging} onClose={() => setLogging(null)} onDone={load} />
    </div>
  );
}

function ComplianceDialog({ x, onClose, onDone }) {
  const [note, setNote] = useState("");
  const submit = async () => {
    try {
      await ujmsLogCompliance(x.condition.id, { status: x.status, note });
      toast.success(x.status === "VIOLATION" ? "VIOLATION_REPORTED — docketed, judge task created" : "Compliance logged");
      onClose(); onDone(); setNote("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!x} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pt-compliance-dialog">
        <DialogHeader><DialogTitle>{x?.status === "VIOLATION" ? "Report violation" : "Log compliance"} — {x?.condition?.condition_label}</DialogTitle></DialogHeader>
        {x?.status === "VIOLATION" && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2.5 py-1.5">
            A violation is entered on the official docket and creates an URGENT review task for the judge.
          </div>
        )}
        <Field label="Note">
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={x?.status === "VIOLATION" ? "Missed check-in on…" : "Weekly check-in completed…"} data-testid="pt-compliance-note" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={x?.status === "VIOLATION" ? btnDanger : btnPrimary} onClick={submit} data-testid="pt-compliance-submit">
            {x?.status === "VIOLATION" ? "Report Violation" : "Log Compliant"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
